import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserPlus, FaTrash, FaCamera, FaImages } from 'react-icons/fa';
import {
  getManagerPhotographers,
  addManagerPhotographer,
  removeManagerPhotographer,
} from '../services/api';

/**
 * An event manager's roster: the photographers whose work they may credit.
 *
 * Photos already credited to someone keep that credit when they leave the
 * roster — the roster governs who can be credited next, not who took what.
 */
const ManagerRoster = () => {
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRoster();
  }, []);

  const fetchRoster = async () => {
    try {
      const response = await getManagerPhotographers();
      setPhotographers(response.photographers || []);
    } catch (err) {
      setError(err.message || 'Impossible de charger votre équipe');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Saisissez l’e-mail du photographe');
      return;
    }

    setAdding(true);
    setError('');
    setSuccess('');
    try {
      const response = await addManagerPhotographer(trimmed);
      setSuccess(`${response.photographer.name} fait maintenant partie de votre équipe.`);
      setEmail('');
      await fetchRoster();
    } catch (err) {
      setError(
        err.message
        || "Aucun compte photographe ne correspond à cet e-mail. Le photographe doit d'abord créer son compte."
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (photographer) => {
    const confirmed = window.confirm(
      `Retirer ${photographer.name} de votre équipe ?\n\n`
      + 'Les photos déjà attribuées gardent son nom ; vous ne pourrez simplement '
      + 'plus lui attribuer de nouvelles photos.'
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      await removeManagerPhotographer(photographer.id);
      setPhotographers(prev => prev.filter(p => p.id !== photographer.id));
      setSuccess(`${photographer.name} a été retiré de votre équipe.`);
    } catch (err) {
      setError(err.message || 'Impossible de retirer ce photographe');
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement de votre équipe...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1>Mon équipe de photographes</h1>
        <p>
          Attribuez chaque lot de photos au photographe qui l'a prise. Les ventes
          restent les vôtres : l'attribution sert à créditer le photographe.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form
        onSubmit={handleAdd}
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          margin: '2rem 0',
          padding: '1.5rem',
          background: 'var(--bg)',
          borderRadius: '12px',
        }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mail du photographe"
          disabled={adding}
          style={{ flex: '1 1 260px', padding: '0.8rem', borderRadius: '8px' }}
        />
        <button type="submit" className="btn-primary" disabled={adding}>
          <FaUserPlus /> {adding ? 'Ajout...' : 'Ajouter à mon équipe'}
        </button>
      </form>

      {photographers.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--surface)',
          borderRadius: '15px',
          boxShadow: '0 4px 8px var(--shadow)',
        }}>
          <FaCamera style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
          <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
            Aucun photographe dans votre équipe
          </h3>
          <p style={{ color: 'var(--gray)' }}>
            Ajoutez un photographe par son e-mail pour pouvoir lui attribuer des
            photos lors de vos imports.
          </p>
        </div>
      ) : (
        <div className="events-list">
          {photographers.map(photographer => (
            <div key={photographer.id} className="event-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' }}>
                <div>
                  <h3 style={{ marginBottom: '0.25rem' }}>{photographer.name}</h3>
                  <p style={{ color: 'var(--gray)', margin: 0, fontSize: '0.9rem' }}>
                    {photographer.email}
                  </p>
                  {photographer.business_name && (
                    <p style={{ color: 'var(--gray)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                      {photographer.business_name}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleRemove(photographer)}
                  title="Retirer de mon équipe"
                  style={{
                    background: '#EF4444', color: 'white', border: 'none',
                    padding: '0.5rem 0.8rem', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  <FaTrash />
                </button>
              </div>
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--border)',
                color: 'var(--gray)',
                fontSize: '0.9rem',
              }}>
                <FaImages /> {photographer.credited_photos_count ?? 0} photo
                {(photographer.credited_photos_count ?? 0) !== 1 ? 's' : ''} attribuée
                {(photographer.credited_photos_count ?? 0) !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <Link to="/photographer">← Retour à mes événements</Link>
      </div>
    </div>
  );
};

export default ManagerRoster;
